import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut as firebaseSignOut, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

// Uses Vite env variables - set these in your environment or .env file (see README update below)
// Use Vite env vars if provided, otherwise fall back to the Firebase config values you supplied.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let app: any = null;
let auth: ReturnType<typeof getAuth> | null = null;
let db: ReturnType<typeof getFirestore> | null = null;

if (!getApps().length && firebaseConfig.apiKey) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  // Initialize analytics only in browser environments where window is present
  try {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // analytics requires a window object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const analytics = getAnalytics(app);
    }
  } catch (e) {
    // analytics initialization may fail in SSR or environments without window - ignore silently
  }
}

const provider = new GoogleAuthProvider();

export const isFirebaseInitialized = () => !!auth && !!db;

export const handleRedirectResult = async () => {
  if (!auth) return null;
  try {
    // getRedirectResult resolves when the page is redirected back from provider
    const result = await getRedirectResult(auth as any);
    return result;
  } catch (err) {
    // Log and rethrow to allow caller to handle UI messaging
    (console as any).error('getRedirectResult failed', err);
    throw err;
  }
};
export const signInWithGoogle = async () => {
  if (!auth) throw new Error('Firebase not initialized');
  try {
    return await signInWithPopup(auth, provider);
  } catch (err: any) {
    // Some browsers block popups or third-party cookies; try redirect fallback for better UX
    // Also fallback on internal errors that can be caused by blocked third-party contexts
    if (err?.code === 'auth/popup-blocked' || err?.code === 'auth/internal-error' || err?.code === 'auth/operation-not-supported-in-this-environment') {
      try {
        await signInWithRedirect(auth, provider);
        return;
      } catch (redirErr) {
        // rethrow original error for caller to handle and log both
        (console as any).error('signInWithRedirect failed after popup error', redirErr);
        throw err;
      }
    }
    throw err;
  }
};

export const signOut = async () => {
  if (!auth) throw new Error('Firebase not initialized');
  return firebaseSignOut(auth);
};

export const onAuthState = (cb: (user: User | null) => void) => {
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
};

// Firestore helpers: user doc is 'users/{uid}/state'
export const getUserState = async (uid: string) => {
  if (!db) throw new Error('Firestore not initialized');
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as any) : null;
};

export const setUserState = async (uid: string, state: any) => {
  if (!db) throw new Error('Firestore not initialized');
  const ref = doc(db, 'users', uid);
  await setDoc(ref, state, { merge: true });
};

export const currentUser = () => auth?.currentUser ?? null;

export default {
  isFirebaseInitialized,
  signInWithGoogle,
  signOut,
  onAuthState,
  getUserState,
  setUserState,
  currentUser,
};
